import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { aiStudioService, AiGeneration } from '@/services/ai-studio.service';
import { Search, Upload, Loader2, ImageIcon, Film, Check, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

export interface PickedAsset {
  url: string;
  kind: 'image' | 'video';
  filename?: string;
  generationUid: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Restrict the grid to one media type; omit to allow both. */
  mediaType?: 'image' | 'video';
  /** Called with the chosen asset. The picker closes itself afterwards. */
  onPick: (asset: PickedAsset) => void;
  title?: string;
}

const PAGE_SIZE = 24;

/**
 * "Choose from library" — a reusable picker over Media Library assets
 * (AI-generated + uploaded). Also offers an inline upload so the operator can
 * add a new file without leaving the builder. Returns a hosted asset URL.
 */
export function MediaLibraryPicker({ open, onClose, mediaType, onPick, title }: Props) {
  const [items, setItems] = useState<AiGeneration[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<AiGeneration | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async (opts: { page: number; q: string }) => {
    setLoading(true);
    try {
      const r = await aiStudioService.list({
        mediaOnly: true,
        mediaType,
        q: opts.q || undefined,
        page: opts.page,
        limit: PAGE_SIZE,
      });
      setItems(r.items);
      setTotal(r.total);
    } catch {
      /* interceptor surfaces */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setQ('');
    setPage(1);
    load({ page: 1, q: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mediaType]);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const mime = file.type || '';
    const isVideo = mime.startsWith('video/');
    const isImage = mime.startsWith('image/');
    if (!isVideo && !isImage) { toast.error('Pick an image or video file'); return; }
    if (mediaType && ((mediaType === 'image' && !isImage) || (mediaType === 'video' && !isVideo))) {
      toast.error(`This field only accepts ${mediaType}s`);
      return;
    }
    // Meta caps: image 5MB, video 16MB (broadcast/template). Library itself
    // allows up to 100MB, but warn early for the common WhatsApp case.
    const capMb = isVideo ? 16 : 5;
    if (file.size > capMb * 1024 * 1024) {
      toast.error(`File too large — keep ${isVideo ? 'videos' : 'images'} under ${capMb}MB for messaging`);
      return;
    }
    setUploading(true);
    try {
      const created = await aiStudioService.uploadMedia(file);
      toast.success('Added to library');
      setItems((prev) => [created, ...prev]);
      setSelected(created);
    } catch {
      /* interceptor surfaces */
    } finally {
      setUploading(false);
    }
  };

  const confirm = () => {
    if (!selected?.assetUrl) return;
    onPick({
      url: selected.assetUrl,
      kind: selected.kind === 'video' ? 'video' : 'image',
      filename: selected.originalFilename ?? selected.title,
      generationUid: selected.generationUid,
    });
    onClose();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Modal open={open} onClose={onClose} title={title ?? 'Choose from library'} widthClass="max-w-3xl">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search by prompt or filename…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load({ page: 1, q }); } }}
            iconLeft={<Search size={14} />}
          />
          <Button variant="secondary" onClick={() => { setPage(1); load({ page: 1, q }); }}>Search</Button>
          <input
            ref={fileRef}
            type="file"
            accept={mediaType === 'video' ? 'video/*' : mediaType === 'image' ? 'image/*' : 'image/*,video/*'}
            className="hidden"
            onChange={onUpload}
          />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} iconLeft={uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}>
            Upload
          </Button>
        </div>

        <div className="min-h-[280px]">
          {loading ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {[...Array(8)].map((_, i) => <div key={i} className="skeleton aspect-square rounded-lg" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-center text-text-muted">
              <Sparkles size={28} className="mb-2 opacity-40" />
              <p className="text-sm font-medium text-text-primary">Nothing in your library yet</p>
              <p className="mt-1 max-w-xs text-xs">Generate assets in AI Studio, or upload a file with the button above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {items.map((g) => {
                const active = selected?.generationUid === g.generationUid;
                return (
                  <button
                    key={g.generationUid}
                    type="button"
                    onClick={() => setSelected(g)}
                    onDoubleClick={() => { setSelected(g); setTimeout(confirm, 0); }}
                    className={clsx(
                      'group relative aspect-square overflow-hidden rounded-lg border bg-surface-alt text-left transition',
                      active ? 'border-primary ring-2 ring-primary-soft' : 'border-border hover:border-primary/50',
                    )}
                    title={g.title ?? g.prompt}
                  >
                    {g.kind === 'image' ? (
                      <img src={g.thumbnailUrl ?? g.assetUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-black/80">
                        <Film size={22} className="text-white/80" />
                      </div>
                    )}
                    <span className="absolute left-1 top-1 inline-flex items-center gap-0.5 rounded bg-black/55 px-1 py-0.5 text-[9px] font-medium uppercase text-white">
                      {g.kind === 'image' ? <ImageIcon size={9} /> : <Film size={9} />}
                      {g.source === 'uploaded' ? 'upload' : 'ai'}
                    </span>
                    {active && (
                      <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-on-primary">
                        <Check size={12} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-text-muted">
            {total > 0 && (
              <>
                <Button size="sm" variant="ghost" disabled={page <= 1 || loading} onClick={() => { const p = page - 1; setPage(p); load({ page: p, q }); }}>Prev</Button>
                <span>Page {page} / {totalPages}</span>
                <Button size="sm" variant="ghost" disabled={page >= totalPages || loading} onClick={() => { const p = page + 1; setPage(p); load({ page: p, q }); }}>Next</Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={confirm} disabled={!selected}>Use this</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
